/*!
 * solo.js — solo(): every sound cut but one, as a musical event
 * © 2026 Rob Canning — GPLv3
 *
 *     solo(sine)                       everything else cut, now -- the sine alone
 *     solo(sine, 4)                    for 4 beats, then everything slams back
 *     solo(sine, 4, quant:4)           the cut on the next bar, back 4 beats later
 *     solo(sine, 4, boost:2)           the sine twice as loud while it is alone
 *     solo(sine, 4, fade:2)            a duck over 2 beats, not a cut
 *     solo(sine, 0.5, trig:every(16, beat.hit)) a half-beat hole every 16 beats
 *     solo([sine, kick], 2)            several -- or a group
 *     solo(off)                        all back now (and a trig: solo no longer comes round)
 *
 * The others drop in 5 ms (a cut, not a click) and come back the same way,
 * each to where its own fader (level()) was; their reverb and echoes are
 * cut too -- but not the bus a soloed sound goes through (bus:room), and
 * solo(room) keeps what is in the room. A soloed sound not playing is
 * started -- from the line it was last run with, or else its line in the
 * console, at the cut -- and, the solo over, goes as the others come
 * back: it is there only for the hole. A sound started during the solo
 * starts silent and comes in with the rest. Times are beats.
 */

import { setSolo, levelArgs, levelOf, soloOwned, soloQuiet, soloRestore, isSounding } from "./level.js";
import { quantStartSec, beatsAt, synthLine } from "../synth.js";
import { sharedAudioCtx } from "./audioShared.js";
import { parseCueToAST } from "../../parser/parser.js";
import { stopSound } from "./audioFile.js";
import { dropSignalTriggers } from "../../control/signalTriggers.js";
import { synthLineIn } from "../../interaction/liveEval.js";
import { handleCueTrigger } from "../cueDispatcher.js";
import { onReset } from "../../system/teardown.js";

const CUT = 0.005;                 // seconds: the cut, fast enough to be one, slow enough not to click
// The sounds solo() started and holds: they go when the solo ends. Kept
// until then -- a solo run again before its end (by hand, or its trig:
// coming round sooner than its length), or solo(off), takes them over;
// they went with the end laid first, which the new one cancels: the sine
// it started played on after every solo.
const held = new Set();
const stopped = new Map();         // name -> when a solo's end stopped it: ringing out a moment, not "sounding"
const justStopped = n => performance.now() - (stopped.get(n) ?? -1e9) < 500;
let endTimers = [];
const cancelEnd = () => { for (const t of endTimers) clearTimeout(t); endTimers = []; };
// hush() (and so a new example and a project switch) ends a solo: kept in
// force, it muted every sound started after it
onReset("sound", function endSolos() {
  cancelEnd();
  held.clear();
  stopped.clear();
  soloOwned.clear();
  setSolo(null, sharedAudioCtx.currentTime, CUT);
});


/**
 * Start `name` from its last line -- or, never run, from its line in the
 * console -- on the grid of the cut (its trig: left off).
 */
function startFromLine(name, quant) {
  const line = synthLine(name) ?? synthLineIn(window.oscillaLiveText?.() ?? "", name);
  if (!line) { console.warn(`[solo] ${name} is not playing, and has no line to start it from -- write or run one`); return false; }
  const ast = parseCueToAST(line);
  if (!ast) return false;
  ast.args = (ast.args || []).filter(a => a.type !== "trig" && a.type !== "quant");
  ast.args.push({ type: "quant", value: quant > 0 ? quant : 0 });
  ast._bySolo = true;              // the dispatcher: not a line run by hand
  soloOwned.add(name);
  handleCueTrigger(ast, false, true, null, { live: true, source: line });
  return true;
}

const LAY_AHEAD = 0.15;            // seconds: the started sounds' going is laid this far ahead of the end

/**
 * End the solo at audio time `at`: the others back; the sounds it started
 * and still holds silent with them, then stopped, their stages back to 1 --
 * so the line run again later sounds. One run by hand meanwhile is kept.
 */
function endSolo(at, fade) {
  cancelEnd();
  setSolo(null, at, fade, 1);
  if (!held.size) return;
  const ms = t => Math.max(0, (t - sharedAudioCtx.currentTime) * 1000);
  // which are still the solo's is asked at the end, not now (a solo run
  // meanwhile cancels all this: they are its own then)
  endTimers.push(setTimeout(() => {
    const gone = [...held];
    const mine = gone.filter(n => soloOwned.has(n));
    soloQuiet(mine, at, fade);
    endTimers.push(setTimeout(() => {
      held.clear();
      const still = mine.filter(n => soloOwned.has(n));
      for (const n of still) { soloOwned.delete(n); stopped.set(n, performance.now()); stopSound(n, 0.01); }
      // stopped (or taken back by hand): the stage up again once the stop has rung out
      endTimers.push(setTimeout(() => { soloRestore(gone); for (const n of still) stopped.delete(n); }, still.length ? 60 : 0));
    }, ms(at + fade) + 30));
  }, ms(at - LAY_AHEAD)));
}

/** solo(...) as a cue. */
export function handleSoloCue(ast) {
  const args = ast?.args || [];
  const arg = k => args.find(a => a.type === k)?.value;
  const a = levelArgs(args);
  const ctx = sharedAudioCtx;
  const names = a.names.filter(n => n !== "off");
  const quant = Number(arg("quant")) > 0 ? Number(arg("quant")) : 0;
  const at = quant > 0 ? quantStartSec(quant) : ctx.currentTime + 0.01;
  const fade = arg("fade") != null ? Math.max(CUT, beatsAt(at, Number(arg("fade")))) : CUT;
  if (!names.length || a.names.includes("off")) {
    // all back -- a solo(…, trig:) line coming round again too
    dropSignalTriggers("live:cueSolo:");
    endSolo(at, fade);
    return;
  }

  const boost = Math.max(0, Math.min(4, Number(arg("boost") ?? 1)));
  // a solo running: what it started goes when this one ends instead (one
  // its end had already stopped is started again, if this one names it)
  cancelEnd();
  for (const n of names) {
    if ((!isSounding(n) || justStopped(n)) && startFromLine(n, quant)) held.add(n);
    stopped.delete(n);
  }
  // levelOf touched so a name with no channel yet is not left out: the
  // started sound's channel is made as it starts, with the solo already in force
  for (const n of names) levelOf(n);
  setSolo(names, at, fade, boost);

  const dur = Number(a.to ?? a.numbers[0]);
  if (Number.isFinite(dur) && dur > 0) endSolo(at + beatsAt(at, dur), fade);
}
